import { Injectable, NgZone, inject } from '@angular/core';
import { Firestore, collection, query, where, DocumentData } from '@firebase/firestore';
import { Observable, Subject, Subscription } from 'rxjs';
import { ConnectorStatus, FirestoreConnectorInfo } from 'src/app/features/charging-station-location/charging-station-location-model';
import { FIRESTORE_INSTANCE } from './firestore-instance.token';
import { observeCollectionData } from './firestore-observables';

@Injectable({
  providedIn: 'root',
})
export class FirestoreService {
  private firestore = inject<Firestore>(FIRESTORE_INSTANCE);
  private ngZone = inject(NgZone);

  private connectorStatusChanged = new Subject<FirestoreConnectorInfo[]>();
  private subscriptionsMap = new Map<string, Array<Subscription>>();

  public setupFireStoreConnectorSubscription(chargingStationLocationIdentifier: string): void {
    const connectorsRef = collection(this.firestore, 'OK/Emsp/ChargingStations/Status/Connectors');
    const connectorsQuery = query(connectorsRef, where('locationId', '==', chargingStationLocationIdentifier));

    const subscription = observeCollectionData(connectorsQuery, this.ngZone, { idField: 'id' }).subscribe(connectors => {
      const firestoreConnectors = connectors.map(conn => this.convertToFirestoreConnectorInfo(conn));
      this.connectorStatusChanged.next(firestoreConnectors);
    });

    this.trackSubscription(chargingStationLocationIdentifier, subscription);
  }

  public setupFireStoreConnectorSubscriptionForChargingStation(chargingStationIdentifier: string): void {
    const connectorsRef = collection(this.firestore, 'OK/Emsp/ChargingStations/Status/Connectors');
    const connectorsQuery = query(connectorsRef, where('chargingStationId', '==', chargingStationIdentifier));

    const subscription = observeCollectionData(connectorsQuery, this.ngZone, { idField: 'id' }).subscribe(connectors => {
      const firestoreConnectors = connectors.map(conn => this.convertToFirestoreConnectorInfo(conn));
      this.connectorStatusChanged.next(firestoreConnectors);
    });

    this.trackSubscription(chargingStationIdentifier, subscription);
  }
  private trackSubscription(key: string, subscription: Subscription): void {
    const existing = this.subscriptionsMap.get(key) ?? [];
    existing.push(subscription);
    this.subscriptionsMap.set(key, existing);
  }

  public tearDownFireStoreConnectorSubscriptions() {
    for (const entry of this.subscriptionsMap.entries()) {
      const chargingStationLocationIdentifier = entry[0];
      const subscriptions: Array<Subscription> = entry[1];
      subscriptions.forEach(subscription => {
        subscription.unsubscribe();
        this.subscriptionsMap.delete(chargingStationLocationIdentifier);
      });
    }
  }

  public connectorStatusChanged$(): Observable<FirestoreConnectorInfo[]> {
    return this.connectorStatusChanged.asObservable();
  }

  private convertToFirestoreConnectorInfo(conn: DocumentData): FirestoreConnectorInfo {
    const { id, status, priceId, pricePerKwhInOere, hasLimitedAccess } = conn;

    if (!id) return { connectorStatus: ConnectorStatus.Unknown } as FirestoreConnectorInfo;

    const [chargingStationIdentifier, connectorNumberStr] = id.split('__');

    return {
      chargingStationLocationIdentifier: conn.locationId ?? '', // Provide a fallback if locationId is missing
      chargingStationIdentifier,
      connectorNumber: parseInt(connectorNumberStr, 10),
      connectorStatus: this.getConnectorStatus(status),
      priceId,
      pricePerKwhInOere,
      hasLimitedAccess,
      firestoreIdentifier: id,
    };
  }

  private getConnectorStatus(status: string): ConnectorStatus {
    let connectorStatus: ConnectorStatus;
    switch (status) {
      case 'Available':
        connectorStatus = ConnectorStatus.Available;
        break;
      case 'Preparing':
      case 'Charging':
      case 'Finishing':
        connectorStatus = ConnectorStatus.Charging;
        break;
      case 'Reserved':
      case 'SuspendedEVSE':
      case 'SuspendedEV':
      case 'Unavailable':
        connectorStatus = ConnectorStatus.Unavailable;
        break;
      case 'Faulted':
        connectorStatus = ConnectorStatus.Faulted;
        break;
      default:
        connectorStatus = ConnectorStatus.Unknown;
        break;
    }

    return connectorStatus;
  }
}
